import {
  advancePlayback,
  type MutablePlaybackState,
  type PlaybackAdvanceInput,
  type PlaybackState,
} from '../engine/animation/advancePlayback.js'
import {
  createDownsampleIndices,
  MAX_PARTICLE_COUNT,
  type DeviceClass,
} from '../engine/particles/particleBudget.js'
import {
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXEL_AREA,
  MIN_CANVAS_DIMENSION,
  type ProjectDocumentV2,
} from '../project/ProjectDocument.js'
import {
  RENDER_INVALIDATION_BITS,
  type MutableRenderFrameView,
  type RenderBackend,
  type RenderBackendStatus,
  type RenderInvalidationReason,
} from './RenderBackend.js'
import {
  createAdaptiveQualityPolicy,
  type AdaptiveQualityPolicy,
  type MutableQualityDecision,
} from './qualityPolicy.js'

export const MAX_FRAME_DELTA_SECONDS = 0.1

export interface MonotonicClock {
  now(): number
}

export interface AnimationFrameSource {
  request(callback: (timestampMs: number) => void): number
  cancel(handle: number): void
}

export type PlaybackSettings = Omit<PlaybackAdvanceInput, 'deltaSeconds'>

export interface RenderSchedulerOptions {
  clock: MonotonicClock
  raf: AnimationFrameSource
  backend: RenderBackend
  frameView: MutableRenderFrameView
  prepareFrame: (frame: MutableRenderFrameView) => void
  initialPlayback: PlaybackState
  playbackSettings: PlaybackSettings
  deviceClass: DeviceClass
  onRenderCommitted?: (backend: RenderBackend) => void
  onPlaybackChange?: (state: PlaybackState) => void
  onError?: (error: unknown, phase: 'prepare' | 'advance' | 'resize' | 'render') => void
}

export type RenderBackendAdoptionResult = 'adopted' | 'unadopted' | 'unchanged'

export interface RenderScheduler {
  invalidate(reason: RenderInvalidationReason): boolean
  play(): boolean
  pause(): boolean
  setAmbientMotion(active: boolean): boolean
  setPlaybackState(state: PlaybackState): boolean
  setPlaybackSettings(settings: PlaybackSettings): boolean
  setVisible(visible: boolean): boolean
  setReducedMotion(reduced: boolean): boolean
  setSurface(width: number, height: number, requestedDpr: number): boolean
  adoptBackend(backend: RenderBackend, deviceClass: DeviceClass): RenderBackendAdoptionResult
  replaceBackend(backend: RenderBackend, deviceClass: DeviceClass): boolean
  recover(): boolean
  dispose(): boolean
}

type TerminalState = 'active' | 'faulted' | 'disposed'
type ErrorPhase = 'prepare' | 'advance' | 'resize' | 'render'

function copyPlayback(source: PlaybackState, destination: MutablePlaybackState): void {
  destination.progress = source.progress
  destination.travelDirection = source.travelDirection
  destination.phase = source.phase
  destination.holdRemaining = source.holdRemaining
  destination.completed = source.completed
}

function samePlayback(left: PlaybackState, right: PlaybackState): boolean {
  return Object.is(left.progress, right.progress)
    && left.travelDirection === right.travelDirection
    && left.phase === right.phase
    && Object.is(left.holdRemaining, right.holdRemaining)
    && left.completed === right.completed
}

function snapshotPlayback(state: PlaybackState): PlaybackState {
  const progress = state.progress
  const travelDirection = state.travelDirection
  const phase = state.phase
  const holdRemaining = state.holdRemaining
  const completed = state.completed
  return { progress, travelDirection, phase, holdRemaining, completed }
}

function snapshotSettings(settings: PlaybackSettings): PlaybackSettings {
  const progressPerSecond = settings.progressPerSecond
  const direction = settings.direction
  const loopMode = settings.loopMode
  const holdAtOrder = settings.holdAtOrder
  const holdAtChaos = settings.holdAtChaos
  return { progressPerSecond, direction, loopMode, holdAtOrder, holdAtChaos }
}

function sameSettings(left: PlaybackSettings, right: PlaybackSettings): boolean {
  return Object.is(left.progressPerSecond, right.progressPerSecond)
    && left.direction === right.direction
    && left.loopMode === right.loopMode
    && Object.is(left.holdAtOrder, right.holdAtOrder)
    && Object.is(left.holdAtChaos, right.holdAtChaos)
}

function assertBoolean(value: boolean, label: string): void {
  if (typeof value !== 'boolean') throw new TypeError(`${label} must be boolean`)
}

function assertSurface(width: number, height: number, requestedDpr: number): void {
  if (
    !Number.isSafeInteger(width)
    || width < MIN_CANVAS_DIMENSION
    || width > MAX_CANVAS_DIMENSION
  ) throw new RangeError('Render surface width is outside the supported canvas range')
  if (
    !Number.isSafeInteger(height)
    || height < MIN_CANVAS_DIMENSION
    || height > MAX_CANVAS_DIMENSION
  ) throw new RangeError('Render surface height is outside the supported canvas range')
  if (width * height > MAX_CANVAS_PIXEL_AREA) {
    throw new RangeError('Render surface canvas area exceeds the supported budget')
  }
  if (!Number.isFinite(requestedDpr) || requestedDpr <= 0) {
    throw new RangeError('Render surface DPR must be finite and positive')
  }
}

function assertEffectiveDpr(effectiveDpr: number): void {
  if (!Number.isFinite(effectiveDpr) || effectiveDpr <= 0) {
    throw new RangeError('Effective render surface DPR must be finite and positive')
  }
}

function assertBackendStatus(status: RenderBackendStatus): void {
  if (
    status !== 'initializing'
    && status !== 'ready'
    && status !== 'context-lost'
    && status !== 'disposed'
  ) throw new RangeError(`Render backend status is invalid: ${String(status)}`)
}

function assertUsableBackendStatus(status: RenderBackendStatus): void {
  assertBackendStatus(status)
  if (status === 'disposed') throw new RangeError('Disposed render backends cannot be scheduled')
}

function assertBackendKind(kind: unknown): asserts kind is 'webgl2' | 'canvas2d' {
  if (kind !== 'webgl2' && kind !== 'canvas2d') {
    throw new RangeError(`Render backend kind is invalid: ${String(kind)}`)
  }
}

function assertDeviceClass(deviceClass: DeviceClass): void {
  if (deviceClass !== 'desktop' && deviceClass !== 'mobile' && deviceClass !== 'constrained') {
    throw new RangeError(`Device class is invalid: ${String(deviceClass)}`)
  }
}

function assertFrameParticles(frame: MutableRenderFrameView): void {
  const sourceCount = frame.particles.sourceCount
  if (!Number.isSafeInteger(sourceCount) || sourceCount < 0 || sourceCount > MAX_PARTICLE_COUNT) {
    throw new RangeError('Frame particle source count is outside the supported budget')
  }
  for (const values of [
    frame.particles.positionX,
    frame.particles.positionY,
    frame.particles.size,
    frame.particles.rotation,
    frame.particles.alpha,
  ]) {
    if (!(values instanceof Float32Array) || values.length < sourceCount) {
      throw new RangeError('Frame particle arrays must cover the source count')
    }
  }
  for (const values of [frame.particles.previousPositionX, frame.particles.previousPositionY]) {
    if (values !== undefined && (!(values instanceof Float32Array) || values.length < sourceCount)) {
      throw new RangeError('Previous-frame particle arrays must cover the source count')
    }
  }
}

export function playbackSettingsFromProject(project: ProjectDocumentV2): PlaybackSettings {
  return {
    progressPerSecond: 1.0 * project.animation.speed,
    direction: project.animation.playbackDirection,
    loopMode: project.animation.loopMode,
    holdAtOrder: project.animation.holdAtOrder,
    holdAtChaos: project.animation.holdAtChaos,
  }
}

type PendingBackendEvent =
  | { readonly type: 'status'; readonly status: RenderBackendStatus }
  | { readonly type: 'invalidation' }

interface BackendSubscriptionBinding {
  scheduler: RenderSchedulerImplementation | null
  readonly backend: RenderBackend
  readonly generation: number
  committed: boolean
  readonly pendingEvents: PendingBackendEvent[]
}

interface BackendSubscription {
  readonly binding: BackendSubscriptionBinding
  readonly unsubscribeStatus: () => void
  readonly unsubscribeInvalidation: () => void
}

class RenderSchedulerImplementation implements RenderScheduler {
  private terminal: TerminalState = 'active'
  private backend: RenderBackend
  private backendStatus: RenderBackendStatus
  private deviceClass: DeviceClass
  private visible = true
  private reducedMotion = false
  private playIntent = false
  private ambientMotion = false
  private dirtyMask = 0
  private queuedHandle: number | null = null
  private queuedFrameToken: number | null = null
  private nextFrameToken = 1
  private inFrame = false
  private lastTimestampMs: number | null = null
  private timeSeconds = 0
  private resizeVersion = 0
  // Start one version behind so the first queued frame performs the initial
  // backend resize instead of presenting the backend's default buffer size.
  private resizedVersion = -1
  private surfaceWidth: number
  private surfaceHeight: number
  private surfaceRequestedDpr: number
  private cachedSourceCount = -1
  private cachedBudget = -1
  private pristineIndices = new Uint32Array(0)
  private exposedIndices = new Uint32Array(0)
  private frameNumber = 0
  private backendSubscriptionGeneration = 0
  private nextBackendSubscriptionGeneration = 1
  private backendSubscription: BackendSubscription | null = null
  private prepareFrameCallback: ((frame: MutableRenderFrameView) => void) | null
  private playbackChangeCallback: ((state: PlaybackState) => void) | null
  private renderCommittedCallback: ((backend: RenderBackend) => void) | null
  private errorCallback: ((error: unknown, phase: ErrorPhase) => void) | null
  private readonly playbackScratch: MutablePlaybackState
  private readonly playbackInput: PlaybackAdvanceInput
  private readonly qualityDecision: MutableQualityDecision = {
    tier: 'high', effectiveDpr: 1, particleBudget: 1,
  }
  private readonly qualityPolicy: AdaptiveQualityPolicy
  private readonly disposedBackends = new WeakSet<RenderBackend>()
  private readonly deferredDisposals = new Set<RenderBackend>()

  constructor(
    private readonly clock: MonotonicClock,
    private readonly raf: AnimationFrameSource,
    backend: RenderBackend,
    private readonly frame: MutableRenderFrameView,
    prepareFrame: (frame: MutableRenderFrameView) => void,
    initialPlayback: PlaybackState,
    playbackSettings: PlaybackSettings,
    deviceClass: DeviceClass,
    onRenderCommitted: ((backend: RenderBackend) => void) | undefined,
    onPlaybackChange: ((state: PlaybackState) => void) | undefined,
    onError: ((error: unknown, phase: ErrorPhase) => void) | undefined,
  ) {
    assertDeviceClass(deviceClass)
    assertSurface(
      frame.surface.width,
      frame.surface.height,
      frame.surface.requestedDpr,
    )
    if (typeof prepareFrame !== 'function') throw new TypeError('Frame preparation callback is required')
    if (onPlaybackChange !== undefined && typeof onPlaybackChange !== 'function') {
      throw new TypeError('Playback observer must be a function')
    }
    if (onRenderCommitted !== undefined && typeof onRenderCommitted !== 'function') {
      throw new TypeError('Render commit observer must be a function')
    }
    if (onError !== undefined && typeof onError !== 'function') {
      throw new TypeError('Render error reporter must be a function')
    }
    const initialBackendKind: unknown = backend.kind
    assertBackendKind(initialBackendKind)
    const initialBackendStatus = backend.getStatus()
    assertUsableBackendStatus(initialBackendStatus)

    const initial = snapshotPlayback(initialPlayback)
    const settings = snapshotSettings(playbackSettings)
    const validationScratch: MutablePlaybackState = { ...initial }
    const validationInput: PlaybackAdvanceInput = { ...settings, deltaSeconds: 0 }
    advancePlayback(initial, validationInput, validationScratch)

    this.backend = backend
    this.backendStatus = initialBackendStatus
    this.deviceClass = deviceClass
    this.surfaceWidth = frame.surface.width
    this.surfaceHeight = frame.surface.height
    this.surfaceRequestedDpr = frame.surface.requestedDpr
    this.prepareFrameCallback = prepareFrame
    this.renderCommittedCallback = onRenderCommitted ?? null
    this.playbackChangeCallback = onPlaybackChange ?? null
    this.errorCallback = onError ?? null
    this.playbackScratch = { ...initial }
    this.playbackInput = { ...settings, deltaSeconds: 0 }
    copyPlayback(initial, frame.playback)
    frame.frameNumber = 0
    frame.invalidationMask = 0
    frame.deltaSeconds = 0
    frame.timeSeconds = 0
    this.qualityPolicy = createAdaptiveQualityPolicy(backend.kind, deviceClass)
    this.restoreOwnedSurfaceAndQuality()

    this.subscribeBackend()
  }

  invalidate(reason: RenderInvalidationReason): boolean {
    if (this.terminal === 'disposed') return false
    const bit = RENDER_INVALIDATION_BITS[reason]
    if (bit === undefined) throw new RangeError(`Render invalidation reason is invalid: ${String(reason)}`)
    if ((this.dirtyMask & bit) !== 0) return false
    this.dirtyMask |= bit
    this.scheduleIfNeeded()
    return true
  }

  play(): boolean {
    if (this.terminal === 'disposed' || this.playIntent) return false
    this.playIntent = true
    this.resetTiming()
    this.markDirty(RENDER_INVALIDATION_BITS.playback)
    return true
  }

  pause(): boolean {
    if (this.terminal === 'disposed' || !this.playIntent) return false
    this.playIntent = false
    if (!this.ambientMotion) this.resetTiming()
    this.markDirty(RENDER_INVALIDATION_BITS.playback)
    return true
  }

  setAmbientMotion(active: boolean): boolean {
    if (this.terminal === 'disposed') return false
    assertBoolean(active, 'Ambient motion')
    if (active === this.ambientMotion) return false
    const orderWasActive = this.activePlayback()
    this.ambientMotion = active
    if (!orderWasActive) this.resetTiming()
    this.markDirty(RENDER_INVALIDATION_BITS.playback)
    return true
  }

  setPlaybackState(state: PlaybackState): boolean {
    if (this.terminal === 'disposed') return false
    const candidate = snapshotPlayback(state)
    const validationInput: PlaybackAdvanceInput = {
      progressPerSecond: this.playbackInput.progressPerSecond,
      direction: this.playbackInput.direction,
      loopMode: this.playbackInput.loopMode,
      holdAtOrder: this.playbackInput.holdAtOrder,
      holdAtChaos: this.playbackInput.holdAtChaos,
      deltaSeconds: 0,
    }
    advancePlayback(candidate, validationInput, this.playbackScratch)
    if (samePlayback(this.frame.playback, candidate)) return false
    copyPlayback(candidate, this.frame.playback)
    copyPlayback(candidate, this.playbackScratch)
    this.resetTiming()
    this.markDirty(RENDER_INVALIDATION_BITS.playback)
    return true
  }

  setPlaybackSettings(settings: PlaybackSettings): boolean {
    if (this.terminal === 'disposed') return false
    const candidate = snapshotSettings(settings)
    const validationInput: PlaybackAdvanceInput = { ...candidate, deltaSeconds: 0 }
    advancePlayback(this.frame.playback, validationInput, this.playbackScratch)
    if (sameSettings(this.playbackInput, candidate)) return false
    this.playbackInput.progressPerSecond = candidate.progressPerSecond
    this.playbackInput.direction = candidate.direction
    this.playbackInput.loopMode = candidate.loopMode
    this.playbackInput.holdAtOrder = candidate.holdAtOrder
    this.playbackInput.holdAtChaos = candidate.holdAtChaos
    copyPlayback(this.frame.playback, this.playbackScratch)
    this.resetTiming()
    this.markDirty(RENDER_INVALIDATION_BITS.playback)
    return true
  }

  setVisible(visible: boolean): boolean {
    if (this.terminal === 'disposed') return false
    assertBoolean(visible, 'Visibility')
    if (visible === this.visible) return false
    this.visible = visible
    this.resetTiming()
    if (!visible) this.cancelQueuedFrame()
    else this.scheduleIfNeeded()
    return true
  }

  setReducedMotion(reduced: boolean): boolean {
    if (this.terminal === 'disposed') return false
    assertBoolean(reduced, 'Reduced motion')
    if (reduced === this.reducedMotion) return false
    this.reducedMotion = reduced
    this.resetTiming()
    if (reduced && this.dirtyMask === 0) this.cancelQueuedFrame()
    else this.scheduleIfNeeded()
    return true
  }

  setSurface(width: number, height: number, requestedDpr: number): boolean {
    if (this.terminal === 'disposed') return false
    assertSurface(width, height, requestedDpr)
    if (
      this.surfaceWidth === width
      && this.surfaceHeight === height
      && Object.is(this.surfaceRequestedDpr, requestedDpr)
    ) {
      this.restoreOwnedSurfaceAndQuality()
      return false
    }
    this.surfaceWidth = width
    this.surfaceHeight = height
    this.surfaceRequestedDpr = requestedDpr
    this.restoreOwnedSurfaceAndQuality()
    this.resizeVersion += 1
    this.markDirty(RENDER_INVALIDATION_BITS.surface)
    return true
  }

  replaceBackend(backend: RenderBackend, deviceClass: DeviceClass): boolean {
    return this.adoptBackend(backend, deviceClass) === 'adopted'
  }

  adoptBackend(backend: RenderBackend, deviceClass: DeviceClass): RenderBackendAdoptionResult {
    if (this.isDisposed()) return 'unadopted'
    if (this.disposedBackends.has(backend)) {
      throw new RangeError('Previously disposed render backends cannot be selected again')
    }
    assertDeviceClass(deviceClass)
    let nextKind: unknown
    try {
      nextKind = backend.kind
    } catch (error) {
      if (this.isDisposed()) return 'unadopted'
      throw error
    }
    if (this.isDisposed()) return 'unadopted'
    assertBackendKind(nextKind)
    let nextStatus: RenderBackendStatus
    try {
      nextStatus = backend.getStatus()
    } catch (error) {
      if (this.isDisposed()) return 'unadopted'
      throw error
    }
    if (this.isDisposed()) return 'unadopted'
    assertUsableBackendStatus(nextStatus)
    if (backend === this.backend) {
      if (deviceClass === this.deviceClass) return 'unchanged'
      this.deviceClass = deviceClass
      this.qualityPolicy.reconfigure(nextKind, deviceClass)
      this.restoreOwnedSurfaceAndQuality()
      this.resizeVersion += 1
      this.resetTiming()
      try {
        this.markDirty(RENDER_INVALIDATION_BITS.quality | RENDER_INVALIDATION_BITS.surface)
      } catch (error) {
        this.report(error, 'render')
      }
      return 'adopted'
    }

    let nextSubscription: BackendSubscription
    try {
      nextSubscription = this.createBackendSubscription(backend)
    } catch (error) {
      if (this.isDisposed()) return 'unadopted'
      throw error
    }
    if (this.isDisposed()) {
      this.releaseBackendSubscription(nextSubscription)
      return 'unadopted'
    }
    const oldBackend = this.backend
    this.deferredDisposals.delete(backend)
    this.unsubscribeBackend()
    if (this.isDisposed()) {
      this.releaseBackendSubscription(nextSubscription)
      return 'unadopted'
    }
    this.backend = backend
    this.backendStatus = nextStatus
    this.deviceClass = deviceClass
    this.qualityPolicy.reconfigure(nextKind, deviceClass)
    this.restoreOwnedSurfaceAndQuality()
    this.resizeVersion += 1
    this.resetTiming()
    this.installBackendSubscription(nextSubscription)
    if (this.inFrame) this.deferredDisposals.add(oldBackend)
    else this.disposeBackendOnce(oldBackend)
    if (this.isDisposed()) return 'adopted'
    try {
      this.markDirty(
        RENDER_INVALIDATION_BITS.backend
        | RENDER_INVALIDATION_BITS.surface
        | RENDER_INVALIDATION_BITS.resource,
      )
    } catch (error) {
      this.report(error, 'render')
    }
    return 'adopted'
  }

  recover(): boolean {
    if (this.terminal !== 'faulted') return false
    this.terminal = 'active'
    this.resetTiming()
    this.markDirty(RENDER_INVALIDATION_BITS.backend)
    return true
  }

  dispose(): boolean {
    if (this.terminal === 'disposed') return false
    this.terminal = 'disposed'
    try { this.cancelQueuedFrame() } catch (error) { this.report(error, 'render') }
    this.dirtyMask = 0
    try { this.unsubscribeBackend() } catch (error) { this.report(error, 'render') }
    this.prepareFrameCallback = null
    this.renderCommittedCallback = null
    this.playbackChangeCallback = null
    if (this.inFrame) this.deferredDisposals.add(this.backend)
    else this.disposeBackendOnce(this.backend)
    if (!this.inFrame) this.flushDeferredDisposals()
    this.errorCallback = null
    return true
  }

  private activePlayback(): boolean {
    return this.playIntent && !this.frame.playback.completed
  }

  private continuousActive(): boolean {
    return !this.reducedMotion && (this.activePlayback() || this.ambientMotion)
  }

  private canQueue(): boolean {
    return this.terminal === 'active'
      && this.visible
      && this.backendStatus === 'ready'
      && !this.inFrame
      && this.queuedHandle === null
      && this.queuedFrameToken === null
      && (this.dirtyMask !== 0 || this.continuousActive())
  }

  private scheduleIfNeeded(): void {
    if (!this.canQueue()) return
    const token = this.nextFrameToken
    this.nextFrameToken += 1
    this.queuedFrameToken = token
    try {
      const handle = this.raf.request((timestampMs) => { this.runFrame(token, timestampMs) })
      if (this.queuedFrameToken === token) this.queuedHandle = handle
    } catch (error) {
      if (this.queuedFrameToken === token) this.queuedFrameToken = null
      throw error
    }
  }

  private cancelQueuedFrame(): void {
    const handle = this.queuedHandle
    this.queuedHandle = null
    this.queuedFrameToken = null
    if (handle !== null) this.raf.cancel(handle)
  }

  private markDirty(mask: number): void {
    this.dirtyMask |= mask
    this.scheduleIfNeeded()
  }

  private resetTiming(): void {
    this.lastTimestampMs = null
    this.qualityPolicy.resetTiming()
  }

  private applyQualityDecision(): void {
    this.qualityPolicy.read(this.frame.surface.requestedDpr, this.qualityDecision)
    this.frame.surface.effectiveDpr = this.qualityDecision.effectiveDpr
    this.frame.quality.tier = this.qualityDecision.tier
    this.frame.quality.particleBudget = this.qualityDecision.particleBudget
  }

  private restoreOwnedSurfaceAndQuality(): void {
    this.frame.surface.width = this.surfaceWidth
    this.frame.surface.height = this.surfaceHeight
    this.frame.surface.requestedDpr = this.surfaceRequestedDpr
    this.applyQualityDecision()
  }

  private reconcilePreparedSurface(): void {
    const surface = this.frame.surface
    assertSurface(surface.width, surface.height, surface.requestedDpr)
    assertEffectiveDpr(surface.effectiveDpr)
    if (
      surface.width !== this.surfaceWidth
      || surface.height !== this.surfaceHeight
      || !Object.is(surface.requestedDpr, this.surfaceRequestedDpr)
    ) {
      this.surfaceWidth = surface.width
      this.surfaceHeight = surface.height
      this.surfaceRequestedDpr = surface.requestedDpr
      this.resizeVersion += 1
      this.frame.invalidationMask |= RENDER_INVALIDATION_BITS.surface
    }
    this.restoreOwnedSurfaceAndQuality()
  }

  private runFrame(token: number, timestampMs: number): void {
    if (token !== this.queuedFrameToken) return
    this.queuedFrameToken = null
    this.queuedHandle = null
    if (
      this.terminal !== 'active'
      || !this.visible
      || this.backendStatus !== 'ready'
    ) return

    this.inFrame = true
    const mask = this.dirtyMask
    this.dirtyMask = 0
    const backend = this.backend
    let rawIntervalMs = 0
    let eligibleQualitySample = false
    let workStart = 0

    try {
      const continuous = this.continuousActive()
      let deltaSeconds = 0
      try {
        if (!Number.isFinite(timestampMs)) throw new RangeError('RAF timestamp must be finite')
        if (continuous) {
          if (this.lastTimestampMs !== null) {
            rawIntervalMs = timestampMs - this.lastTimestampMs
            if (!Number.isFinite(rawIntervalMs) || rawIntervalMs < 0) {
              throw new RangeError('RAF timestamp must be monotonic')
            }
            deltaSeconds = Math.min(rawIntervalMs / 1_000, MAX_FRAME_DELTA_SECONDS)
            eligibleQualitySample = true
          }
          this.lastTimestampMs = timestampMs
        } else {
          this.lastTimestampMs = null
        }

        if (this.activePlayback()) {
          this.playbackInput.deltaSeconds = deltaSeconds
          advancePlayback(this.frame.playback, this.playbackInput, this.playbackScratch)
          const changed = !samePlayback(this.frame.playback, this.playbackScratch)
          copyPlayback(this.playbackScratch, this.frame.playback)
          if (changed) this.notifyPlaybackChange()
        }
        if (continuous) this.timeSeconds += deltaSeconds
        this.frame.deltaSeconds = deltaSeconds
        this.frame.timeSeconds = this.timeSeconds
        this.frame.invalidationMask = mask
        this.frameNumber += 1
        this.frame.frameNumber = this.frameNumber
      } catch (error) {
        this.fault(error, 'advance', mask)
        return
      }

      try {
        workStart = this.clock.now()
      } catch (error) {
        this.fault(error, 'prepare', mask)
        return
      }
      try {
        const prepare = this.prepareFrameCallback
        if (prepare === null) return
        prepare(this.frame)
        assertFrameParticles(this.frame)
        this.reconcilePreparedSurface()
        this.updateDownsampleIndices()
      } catch (error) {
        this.restoreOwnedSurfaceAndQuality()
        this.fault(error, 'prepare', mask)
        return
      }

      if (!this.canSubmitTo(backend)) {
        if (!this.isDisposed()) this.dirtyMask |= mask
        return
      }

      const resizeVersion = this.resizeVersion
      if (resizeVersion !== this.resizedVersion) {
        try {
          backend.resize(this.frame.surface)
          if (this.backend === backend && this.resizeVersion === resizeVersion) {
            this.resizedVersion = resizeVersion
          }
        } catch (error) {
          this.fault(error, 'resize', mask)
          return
        }
      }
      if (this.resizeVersion !== resizeVersion) {
        if (!this.isDisposed()) this.dirtyMask |= mask
        return
      }
      if (!this.canSubmitTo(backend)) {
        if (!this.isDisposed()) this.dirtyMask |= mask
        return
      }

      try {
        backend.render(this.frame)
        this.renderCommittedCallback?.(backend)
      } catch (error) {
        this.fault(error, 'render', mask)
        return
      }

      if (
        this.terminal === 'active'
        && this.backend === backend
        && eligibleQualitySample
      ) {
        let workEnd: number
        try {
          workEnd = this.clock.now()
        } catch (error) {
          this.fault(error, 'render', mask)
          return
        }
        const workDurationMs = workEnd - workStart
        if (this.qualityPolicy.sample(rawIntervalMs, workDurationMs)) {
          this.applyQualityDecision()
          this.resizeVersion += 1
          this.dirtyMask |= RENDER_INVALIDATION_BITS.quality | RENDER_INVALIDATION_BITS.surface
        }
      }
    } finally {
      this.inFrame = false
      this.flushDeferredDisposals()
      this.scheduleIfNeeded()
    }
  }

  private updateDownsampleIndices(): void {
    const sourceCount = this.frame.particles.sourceCount
    const budget = this.frame.quality.particleBudget
    if (sourceCount !== this.cachedSourceCount || budget !== this.cachedBudget) {
      this.pristineIndices = createDownsampleIndices(sourceCount, budget)
      this.exposedIndices = new Uint32Array(this.pristineIndices.length)
      this.cachedSourceCount = sourceCount
      this.cachedBudget = budget
    }
    this.exposedIndices.set(this.pristineIndices)
    this.frame.particles.indices = this.exposedIndices
    this.frame.particles.renderCount = Math.min(sourceCount, budget)
  }

  private canSubmitTo(backend: RenderBackend): boolean {
    return this.terminal === 'active'
      && this.visible
      && this.backendStatus === 'ready'
      && this.backend === backend
  }

  private isDisposed(): boolean {
    return this.terminal === 'disposed'
  }

  private notifyPlaybackChange(): void {
    const callback = this.playbackChangeCallback
    if (callback === null) return
    try { callback(this.frame.playback) } catch { /* Playback observers cannot fault rendering. */ }
  }

  private report(error: unknown, phase: ErrorPhase): void {
    const reporter = this.errorCallback
    if (reporter === null) return
    try { reporter(error, phase) } catch { /* Reporting cannot affect scheduler ownership. */ }
  }

  private fault(error: unknown, phase: ErrorPhase, mask: number): void {
    if (this.terminal !== 'active') return
    this.terminal = 'faulted'
    this.dirtyMask |= mask
    this.cancelQueuedFrame()
    this.resetTiming()
    this.report(error, phase)
  }

  private handleBackendStatus(status: RenderBackendStatus): void {
    if (this.terminal === 'disposed') return
    assertBackendStatus(status)
    if (status === this.backendStatus) return
    this.backendStatus = status
    this.resetTiming()
    if (status !== 'ready') {
      this.cancelQueuedFrame()
      return
    }
    this.resizeVersion += 1
    this.markDirty(
      RENDER_INVALIDATION_BITS.context
      | RENDER_INVALIDATION_BITS.resource
      | RENDER_INVALIDATION_BITS.surface,
    )
  }

  private subscribeBackend(): void {
    this.installBackendSubscription(this.createBackendSubscription(this.backend))
  }

  private createBackendSubscription(backend: RenderBackend): BackendSubscription {
    const generation = this.nextBackendSubscriptionGeneration
    this.nextBackendSubscriptionGeneration += 1
    const binding: BackendSubscriptionBinding = {
      scheduler: this,
      backend,
      generation,
      committed: false,
      pendingEvents: [],
    }
    const onStatus = (status: RenderBackendStatus): void => {
      const scheduler = binding.scheduler
      if (scheduler === null) return
      assertBackendStatus(status)
      if (!binding.committed) {
        binding.pendingEvents.push({ type: 'status', status })
        return
      }
      scheduler.acceptBackendStatus(binding.backend, binding.generation, status)
    }
    const onInvalidation = (): void => {
      const scheduler = binding.scheduler
      if (scheduler === null) return
      if (!binding.committed) {
        binding.pendingEvents.push({ type: 'invalidation' })
        return
      }
      scheduler.acceptBackendInvalidation(binding.backend, binding.generation)
    }
    let unsubscribeStatus: (() => void) | null = null
    let unsubscribeInvalidation: (() => void) | null = null
    try {
      unsubscribeStatus = backend.subscribeStatus(onStatus)
      if (this.isDisposed()) {
        throw new Error('Render scheduler was disposed during backend status subscription')
      }
      unsubscribeInvalidation = backend.subscribeInvalidation(onInvalidation)
      if (this.isDisposed()) {
        throw new Error('Render scheduler was disposed during backend invalidation subscription')
      }
      if (typeof unsubscribeStatus !== 'function' || typeof unsubscribeInvalidation !== 'function') {
        throw new TypeError('Backend subscriptions must return unsubscribe functions')
      }
      return { binding, unsubscribeStatus, unsubscribeInvalidation }
    } catch (error) {
      binding.scheduler = null
      binding.pendingEvents.length = 0
      try { unsubscribeInvalidation?.() } catch { /* Partial backend subscription cleanup is best-effort. */ }
      try { unsubscribeStatus?.() } catch { /* Partial backend subscription cleanup is best-effort. */ }
      throw error
    }
  }

  private installBackendSubscription(subscription: BackendSubscription): void {
    this.backendSubscription = subscription
    this.backendSubscriptionGeneration = subscription.binding.generation
    subscription.binding.committed = true
    for (const event of subscription.binding.pendingEvents) {
      if (subscription.binding.scheduler !== this) break
      try {
        if (event.type === 'status') {
          this.acceptBackendStatus(
            subscription.binding.backend,
            subscription.binding.generation,
            event.status,
          )
        } else {
          this.acceptBackendInvalidation(
            subscription.binding.backend,
            subscription.binding.generation,
          )
        }
      } catch (error) {
        this.report(error, 'render')
      }
    }
    subscription.binding.pendingEvents.length = 0
  }

  private releaseBackendSubscription(subscription: BackendSubscription): void {
    subscription.binding.scheduler = null
    subscription.binding.pendingEvents.length = 0
    try { subscription.unsubscribeStatus() } catch { /* Unadopted candidate cleanup is best-effort. */ }
    try { subscription.unsubscribeInvalidation() } catch { /* Unadopted candidate cleanup is best-effort. */ }
  }

  private unsubscribeBackend(): void {
    const subscription = this.backendSubscription
    this.backendSubscription = null
    this.backendSubscriptionGeneration = 0
    if (subscription === null) return
    subscription.binding.scheduler = null
    subscription.binding.pendingEvents.length = 0
    try { subscription.unsubscribeStatus() } catch { /* Backend teardown is best-effort and terminal-safe. */ }
    try { subscription.unsubscribeInvalidation() } catch { /* Backend teardown is best-effort and terminal-safe. */ }
  }

  private acceptBackendStatus(
    backend: RenderBackend,
    generation: number,
    status: RenderBackendStatus,
  ): void {
    if (!this.isCurrentBackendSubscription(backend, generation)) return
    this.handleBackendStatus(status)
  }

  private acceptBackendInvalidation(backend: RenderBackend, generation: number): void {
    if (!this.isCurrentBackendSubscription(backend, generation)) return
    this.invalidate('resource')
  }

  private isCurrentBackendSubscription(backend: RenderBackend, generation: number): boolean {
    return this.terminal !== 'disposed'
      && this.backend === backend
      && this.backendSubscriptionGeneration === generation
  }

  private disposeBackendOnce(backend: RenderBackend): void {
    if (this.disposedBackends.has(backend)) return
    this.disposedBackends.add(backend)
    try { backend.dispose() } catch { /* Disposal cannot revive scheduler work. */ }
  }

  private flushDeferredDisposals(): void {
    for (const backend of this.deferredDisposals) this.disposeBackendOnce(backend)
    this.deferredDisposals.clear()
  }
}

export function createRenderScheduler(options: RenderSchedulerOptions): RenderScheduler {
  return new RenderSchedulerImplementation(
    options.clock,
    options.raf,
    options.backend,
    options.frameView,
    options.prepareFrame,
    options.initialPlayback,
    options.playbackSettings,
    options.deviceClass,
    options.onRenderCommitted,
    options.onPlaybackChange,
    options.onError,
  )
}
